/**
 * @fileoverview Página responsável por gerenciar integrações externas da empresa.
 * Hoje suporta WooCommerce (catálogo do Agente IA), OpenAI (chave por empresa
 * para todas as features de IA), a fonte de ordens de serviço (OS
 * enviadas pelo coletor do posto), a conta do Contele (onde a triagem
 * concluída cria o Local da OS), as duas só do super admin, e o Care (avisa as
 * mudanças de status das OS para os fluxos mandarem mensagem; o admin da conta
 * cadastra). Fluxo de criação: clique em "Nova integração"
 * abre um seletor de tipo; ao escolher, abre o formulário específico com botão
 * "Testar conexão" antes de salvar. Modais seguem o padrão visual moderno
 * (Gerenciar Etiquetas / Adicionar anotação) — radius 24, gradient header.
 */

import React, { useState, useEffect, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import {
  Dialog,
  Button,
  IconButton,
  InputBase,
  MenuItem,
  Tooltip,
  Switch,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Plus,
  ShoppingBag,
  Sparkles,
  Trash2,
  RefreshCw,
  Power,
  CheckCircle2,
  AlertCircle,
  Edit2,
  Search,
  Cable,
  X,
  ChevronRight,
  Eye,
  EyeOff,
  BarChart3,
  Lock,
  ClipboardList,
  KeyRound,
  Copy,
  ExternalLink,
  MapPinned,
  Webhook,
} from "lucide-react";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { formatRelativeTime } from "../../utils/formatRelativeTime";
import ConfirmationModal from "../../components/ConfirmationModal";
import ModernSelect from "../../components/ModernSelect";
import OpenAiUsageModal from "../../components/OpenAiUsageModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission from "../../hooks/usePermission";
import { API_BASE_URL } from "../../services/api";

const TYPE_WOOCOMMERCE = "woocommerce";
const TYPE_OPENAI = "openai";
const TYPE_WORK_ORDERS = "work_orders";
const TYPE_CONTELE = "contele";
const TYPE_CARE = "care";

// Estado do coletor de OS (calculado no backend a partir do último sinal).
const WORK_ORDER_HEALTH = {
  online: { label: "Online", tone: "ok", Icon: CheckCircle2 },
  offline: { label: "Sem sinal", tone: "error", Icon: AlertCircle },
  needs_login: { label: "Pedindo login", tone: "pending", Icon: AlertCircle },
  error: { label: "Com erro", tone: "error", Icon: AlertCircle },
  never: { label: "Aguardando o coletor", tone: "pending", Icon: RefreshCw },
  inactive: { label: "Inativa", tone: "inactive", Icon: Power },
};

const INTEGRATION_TYPES = [
  {
    id: TYPE_WOOCOMMERCE,
    label: "WooCommerce",
    description: "Sincroniza catálogo de produtos para que o Agente IA consulte durante o atendimento.",
    Icon: ShoppingBag,
    accent: "#9333ea",
  },
  {
    id: TYPE_OPENAI,
    label: "OpenAI",
    description: "Chave usada por todas as features de IA da empresa (Agente IA, resumos, reformulação).",
    Icon: Sparkles,
    accent: "#22c55e",
  },
  {
    id: TYPE_WORK_ORDERS,
    label: "Ordens de serviço",
    description: "Recebe as OS do sistema do fabricante (ex.: portal ICS da Midea), enviadas pelo coletor instalado no posto.",
    Icon: ClipboardList,
    accent: "#64748b",
    // Só o super admin cria e mexe nesta integração (decisão de 28/09/2026).
    superOnly: true,
  },
  {
    id: TYPE_CONTELE,
    label: "Contele",
    description: "Cria no Contele Teams o Local da OS com a triagem concluída, para o time agendar a visita.",
    Icon: MapPinned,
    accent: "#64748b",
    // Só o super admin cria e mexe nesta integração (decisão de 16/10/2026).
    superOnly: true,
  },
  {
    id: TYPE_CARE,
    label: "Care",
    description: "Recebe as mudanças de status das OS do Care, para os fluxos mandarem mensagem ao cliente.",
    Icon: Webhook,
    accent: "#64748b",
  },
];

const INITIAL_FORM = {
  type: TYPE_WOOCOMMERCE,
  name: "",
  isActive: true,
  // WooCommerce
  storeUrl: "",
  consumerKey: "",
  consumerSecret: "",
  syncIntervalHours: 24,
  // OpenAI
  apiKey: "",
  organizationId: "",
  baseUrl: "",
  // Ordens de serviço
  adapter: "midea-ics",
  alertAfterMinutes: 30,
  // Contele (na edição chegam mascaradas: o backend mantém as gravadas)
  conteleToken: "",
  conteleApiKey: "",
};

const buildPayloadConfig = (form) => {
  // Care: nada a configurar (o endereço é gerado pelo servidor).
  if (form.type === TYPE_CARE) return {};
  if (form.type === TYPE_CONTELE) {
    return { token: form.conteleToken.trim(), apiKey: form.conteleApiKey.trim() };
  }
  if (form.type === TYPE_WORK_ORDERS) {
    return {
      adapter: form.adapter,
      alertAfterMinutes: Number(form.alertAfterMinutes) || 30,
    };
  }
  if (form.type === TYPE_OPENAI) {
    return {
      apiKey: form.apiKey,
      organizationId: form.organizationId.trim() || undefined,
      baseUrl: form.baseUrl.trim() || undefined,
    };
  }
  return {
    storeUrl: form.storeUrl.trim(),
    consumerKey: form.consumerKey.trim(),
    consumerSecret: form.consumerSecret,
    syncIntervalHours: Number(form.syncIntervalHours) || 24,
  };
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  // Identidade visual da página alinhada ao botão "Adicionar fila" em /queues:
  // gradient azul #4f7cff → #3b82f6. Tons de hover/borda derivados do mesmo
  // ponto base (#4f7cff) para manter consistência.
  const accent = "#3b82f6";
  const accentRgb = "79, 124, 255";
  const textPrimary = isDark ? "#f0f2f5" : "#111318";
  const textSecondary = isDark ? "#a0aec0" : "#5a6478";
  const textFaint = isDark ? "#5a6478" : "#a0aec0";
  const border = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)";
  const surfaceCard = isDark ? "rgba(17, 19, 24, 0.85)" : "rgba(255, 255, 255, 0.9)";
  const surfaceHover = isDark
    ? `rgba(${accentRgb}, 0.06)`
    : `rgba(${accentRgb}, 0.04)`;

  const modalBorder = isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.08)";
  const modalDividerBorder = isDark
    ? "1px solid rgba(255, 255, 255, 0.06)"
    : "1px solid rgba(15, 23, 42, 0.08)";
  const modalInputBorder = isDark
    ? "1px solid rgba(255, 255, 255, 0.12)"
    : "1px solid rgba(15, 23, 42, 0.12)";
  const modalInputBg = isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(248, 250, 252, 0.9)";
  const modalTextPrimary = isDark ? "#f8fafc" : "#111827";
  const modalTextMuted = isDark ? "rgba(248, 250, 252, 0.6)" : "#6b7280";

  return {
    page: {
      padding: "28px 32px 40px",
      minHeight: "100%",
      fontFamily: "'Montserrat', sans-serif",
      "& *": { fontFamily: "'Montserrat', sans-serif" },
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 24,
    },
    title: {
      fontSize: 22,
      fontWeight: 600,
      color: textPrimary,
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    subtitle: {
      fontSize: 12,
      color: textSecondary,
      marginTop: 4,
    },
    btnPrimary: {
      whiteSpace: "nowrap",
      flexShrink: 0,
      height: 36,
      padding: "0 14px",
      borderRadius: 10,
      fontSize: "0.875rem",
      fontWeight: 500,
      textTransform: "none",
      gap: 8,
      border: "none",
      boxShadow: "none",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#ffffff",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
        boxShadow: "none",
      },
      "& svg": { flexShrink: 0 },
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
      gap: 16,
    },
    card: {
      background: surfaceCard,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: 16,
      display: "flex",
      flexDirection: "column",
      gap: 12,
      transition: "all 160ms",
      "&:hover": {
        background: surfaceHover,
        borderColor: `rgba(${accentRgb}, 0.3)`,
      },
    },
    cardHeader: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 8,
    },
    cardIcon: {
      width: 36,
      height: 36,
      borderRadius: 8,
      background: `rgba(${accentRgb}, 0.12)`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: accent,
      flexShrink: 0,
    },
    cardName: {
      fontSize: 15,
      fontWeight: 600,
      color: textPrimary,
      marginBottom: 2,
    },
    cardUrl: {
      fontSize: 11,
      color: textFaint,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    cardMetaRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontSize: 11,
      color: textSecondary,
      paddingTop: 8,
      borderTop: `1px solid ${border}`,
    },
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 8px",
      borderRadius: 12,
      fontSize: 10,
      fontWeight: 500,
    },
    // No claro, verde/vermelho/âmbar 500 sobre fundo branco somem: tons 700.
    statusOk: {
      background: isDark ? "rgba(34, 197, 94, 0.12)" : "rgba(22, 163, 74, 0.10)",
      color: isDark ? "#22c55e" : "#15803d",
    },
    statusError: {
      background: isDark ? "rgba(239, 68, 68, 0.12)" : "rgba(220, 38, 38, 0.08)",
      color: isDark ? "#ef4444" : "#b91c1c",
    },
    statusPending: {
      background: isDark ? "rgba(245, 158, 11, 0.12)" : "rgba(217, 119, 6, 0.10)",
      color: isDark ? "#f59e0b" : "#b45309",
    },
    statusInactive: {
      background: "rgba(160, 174, 192, 0.12)",
      color: textFaint,
    },
    managedPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "4px 10px",
      borderRadius: 12,
      fontSize: 10,
      fontWeight: 600,
      background: "rgba(147, 51, 234, 0.12)",
      color: "#9333ea",
      cursor: "default",
      whiteSpace: "nowrap",
    },
    sttelarPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "4px 10px",
      borderRadius: 12,
      fontSize: 10,
      fontWeight: 600,
      background: isDark ? "rgba(148, 163, 184, 0.14)" : "rgba(71, 85, 105, 0.10)",
      color: isDark ? "#cbd5e1" : "#334155",
      cursor: "default",
      whiteSpace: "nowrap",
    },
    cardInfo: {
      fontSize: 11,
      color: textSecondary,
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
    },
    cardNotice: {
      padding: 8,
      borderRadius: 6,
      fontSize: 11,
      wordBreak: "break-word",
      background: isDark ? "rgba(239, 68, 68, 0.08)" : "rgba(220, 38, 38, 0.06)",
      color: isDark ? "#fca5a5" : "#b91c1c",
    },
    cardActions: {
      display: "flex",
      gap: 6,
      flexWrap: "wrap",
    },
    actionBtn: {
      padding: "6px 10px",
      fontSize: 11,
      borderRadius: 6,
      border: `1px solid ${border}`,
      background: "transparent",
      color: textPrimary,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      transition: "all 120ms",
      "&:hover": {
        background: surfaceHover,
        borderColor: accent,
      },
    },
    actionDanger: {
      color: isDark ? "#ef4444" : "#b91c1c",
      "&:hover": { borderColor: isDark ? "#ef4444" : "#b91c1c" },
    },
    searchInput: {
      width: "100%",
      padding: "10px 14px 10px 36px",
      fontSize: 13,
      borderRadius: 8,
      border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.18)",
      background: isDark ? "rgba(0,0,0,0.2)" : "#ffffff",
      color: "inherit",
      outline: "none",
    },
    empty: {
      padding: "60px 20px",
      textAlign: "center",
      color: textSecondary,
      fontSize: 13,
      border: `1px dashed ${border}`,
      borderRadius: 12,
    },

    // ======== Modal (padrão tagModalDialog) ========
    modal: {
      "& .MuiDialog-paper": {
        fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
        width: "min(520px, calc(100vw - 32px))",
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "min(92vh, 760px)",
        borderRadius: 24,
        overflow: "hidden",
        background: isDark
          ? "linear-gradient(180deg, #171a20 0%, #111319 100%)"
          : "#ffffff",
        boxShadow: isDark
          ? "0 24px 80px rgba(0, 0, 0, 0.45)"
          : "0 24px 60px rgba(15, 23, 42, 0.12)",
        border: modalBorder,
        "& *": {
          fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      },
    },
    modalHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(2.5, 3),
      borderBottom: modalDividerBorder,
    },
    modalHeaderText: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
      minWidth: 0,
    },
    modalTitle: {
      fontSize: "1.35rem",
      fontWeight: 700,
      color: modalTextPrimary,
      lineHeight: 1.2,
    },
    modalSubtitle: {
      fontSize: "0.82rem",
      color: modalTextMuted,
    },
    modalClose: {
      color: isDark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
    },
    modalContent: {
      padding: theme.spacing(3),
      maxHeight: "calc(min(92vh, 760px) - 168px)",
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      ...theme.scrollbarStylesSoft,
    },
    modalActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: theme.spacing(1),
      padding: theme.spacing(2, 3, 2.5),
      borderTop: modalDividerBorder,
      flexWrap: "wrap",
    },
    modalCancelBtn: {
      minWidth: 110,
      height: 36,
      borderRadius: 10,
      textTransform: "none",
      fontSize: "0.95rem",
      fontWeight: 500,
      color: modalTextPrimary,
      background: "transparent",
      "&:hover": { background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)" },
    },
    modalGhostBtn: {
      minWidth: 110,
      height: 36,
      borderRadius: 10,
      textTransform: "none",
      fontSize: "0.95rem",
      fontWeight: 500,
      color: modalTextPrimary,
      background: "transparent",
      border: modalInputBorder,
      "&:hover": { background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)" },
    },
    modalPrimaryBtn: {
      minWidth: 110,
      height: 36,
      borderRadius: 10,
      textTransform: "none",
      fontSize: "0.95rem",
      fontWeight: 500,
      boxShadow: "none",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#ffffff",
      "&:hover": {
        boxShadow: "none",
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
      },
    },

    // ── Modal A: seletor de tipo ──
    typePickList: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.5),
    },
    typePickCard: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      padding: theme.spacing(2),
      borderRadius: 18,
      border: modalInputBorder,
      background: isDark ? "rgba(255, 255, 255, 0.02)" : "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
      cursor: "pointer",
      textAlign: "left",
      color: modalTextPrimary,
      transition: "all 160ms",
      "&:hover": {
        borderColor: "#4f7cff",
        background: isDark ? "rgba(79, 124, 255, 0.06)" : "rgba(79, 124, 255, 0.04)",
        transform: "translateY(-1px)",
      },
    },
    typePickIconWrap: {
      width: 44,
      height: 44,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    typePickBody: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    typePickName: {
      fontSize: "1rem",
      fontWeight: 600,
      color: modalTextPrimary,
    },
    typePickDescription: {
      fontSize: "0.83rem",
      color: modalTextMuted,
      lineHeight: 1.4,
    },
    typePickChevron: {
      color: modalTextMuted,
      flexShrink: 0,
    },

    // ── Modal B: formulário ──
    formField: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },
    formLabel: {
      fontSize: "0.78rem",
      fontWeight: 600,
      color: modalTextMuted,
      textTransform: "uppercase",
      letterSpacing: 0.4,
    },
    formInputWrap: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(0.7, 1.6),
      borderRadius: 14,
      border: modalInputBorder,
      background: modalInputBg,
      transition: "border-color 120ms, box-shadow 120ms",
      "&:focus-within": {
        borderColor: "#4f7cff",
        boxShadow: "0 0 0 1px rgba(79, 124, 255, 0.18)",
      },
    },
    formInput: {
      flex: 1,
      color: modalTextPrimary,
      fontSize: "0.95rem",
      "& input::placeholder": {
        color: modalTextMuted,
        opacity: 1,
      },
    },
    formInputAction: {
      padding: 4,
      color: modalTextMuted,
      "&:hover": { color: modalTextPrimary },
    },
    formHelp: {
      fontSize: "0.78rem",
      color: modalTextMuted,
      lineHeight: 1.4,
    },
    formActiveRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.2),
      padding: theme.spacing(1.2, 1.6),
      borderRadius: 14,
      border: modalInputBorder,
      background: modalInputBg,
    },
    formActiveLabel: {
      fontSize: "0.95rem",
      color: modalTextPrimary,
      fontWeight: 500,
    },
    // Select padrão do app (ModernSelect), com a fonte dos campos ao lado.
    formSelect: {
      "& .MuiSelect-select": { fontSize: "0.95rem" },
    },

    // ── Token da fonte de OS (mostrado uma vez) ──
    tokenWarning: {
      fontSize: "0.85rem",
      color: modalTextPrimary,
      lineHeight: 1.5,
      padding: theme.spacing(1.5, 2),
      borderRadius: 14,
      border: modalInputBorder,
      background: modalInputBg,
    },
    tokenValue: {
      flex: 1,
      minWidth: 0,
      wordBreak: "break-all",
      fontSize: "0.82rem",
      color: modalTextPrimary,
      padding: "6px 0",
    },
  };
});

const Integrations = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user, impersonation } = useContext(AuthContext);
  // Fonte de OS: só o super admin mexe (fora da impersonação de usuário). O
  // backend confere de novo.
  const canManageSources = !!user?.super && impersonation?.mode !== "user";
  // A lista de OS recebidas pede work_orders.view, com a regra nova ligada.
  const canSeeWorkOrders = usePermission().can("work_orders.view", true);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typePickOpen, setTypePickOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingTest, setSavingTest] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [testingForm, setTestingForm] = useState(false);
  const [search, setSearch] = useState("");

  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showConsumerSecret, setShowConsumerSecret] = useState(false);
  const [showConteleToken, setShowConteleToken] = useState(false);
  const [showConteleApiKey, setShowConteleApiKey] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [usageItem, setUsageItem] = useState(null);
  // Token da fonte de OS em texto aberto: só logo depois de criar ou gerar outro.
  const [tokenReveal, setTokenReveal] = useState(null);
  const [pendingRegenerate, setPendingRegenerate] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations");
      setItems(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openTypePick = () => {
    setEditing(null);
    setTypePickOpen(true);
  };

  const hideSecrets = () => {
    setShowApiKey(false);
    setShowConsumerSecret(false);
    setShowConteleToken(false);
    setShowConteleApiKey(false);
  };

  const pickType = (type) => {
    setForm({ ...INITIAL_FORM, type });
    hideSecrets();
    setTypePickOpen(false);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    hideSecrets();
    if (item.type === TYPE_CONTELE) {
      // Chaves em branco: a máscara atual aparece embaixo do campo (digitar
      // depois da máscara manteria a chave antiga sem avisar).
      setForm({
        ...INITIAL_FORM,
        type: TYPE_CONTELE,
        name: item.name,
        isActive: item.isActive,
      });
    } else if (item.type === TYPE_OPENAI) {
      setForm({
        ...INITIAL_FORM,
        type: TYPE_OPENAI,
        name: item.name,
        apiKey: item.config?.apiKeyMask || "",
        organizationId: item.config?.organizationId || "",
        baseUrl: item.config?.baseUrl || "",
        isActive: item.isActive,
      });
    } else if (item.type === TYPE_CARE) {
      setForm({ ...INITIAL_FORM, type: TYPE_CARE, name: item.name, isActive: item.isActive });
    } else if (item.type === TYPE_WORK_ORDERS) {
      setForm({
        ...INITIAL_FORM,
        type: TYPE_WORK_ORDERS,
        name: item.name,
        adapter: item.config?.adapter || "midea-ics",
        alertAfterMinutes: item.config?.alertAfterMinutes || 30,
        isActive: item.isActive,
      });
    } else {
      setForm({
        ...INITIAL_FORM,
        type: TYPE_WOOCOMMERCE,
        name: item.name,
        storeUrl: item.config?.storeUrl || "",
        consumerKey: item.config?.consumerKey || "",
        consumerSecret: item.config?.consumerSecretMask || "",
        syncIntervalHours: item.config?.syncIntervalHours || 24,
        isActive: item.isActive,
      });
    }
    setModalOpen(true);
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      toast.error("Informe um nome para a integração");
      return false;
    }
    if (form.type === TYPE_CARE) return true;
    if (form.type === TYPE_CONTELE) {
      // Na edição, campo vazio ou mascarado mantém a chave gravada.
      if (!editing && (!form.conteleToken.trim() || !form.conteleApiKey.trim())) {
        toast.error("Informe o Token e a X-API-KEY do Contele");
        return false;
      }
      return true;
    }
    if (form.type === TYPE_WORK_ORDERS) {
      const minutes = Number(form.alertAfterMinutes);
      if (!Number.isFinite(minutes) || minutes < 15 || minutes > 1440) {
        toast.error("O alerta deve ficar entre 15 e 1440 minutos");
        return false;
      }
      return true;
    }
    if (form.type === TYPE_OPENAI) {
      if (!editing && !form.apiKey.trim()) {
        toast.error("Informe a API key da OpenAI");
        return false;
      }
      return true;
    }
    if (!form.storeUrl.trim()) {
      toast.error("Informe a URL da loja");
      return false;
    }
    if (!editing && !form.consumerKey.trim()) {
      toast.error("Informe o consumer key");
      return false;
    }
    if (!editing && !form.consumerSecret.trim()) {
      toast.error("Informe o consumer secret");
      return false;
    }
    return true;
  };

  const handleTestForm = async () => {
    const blank = (value) => !value.trim() || value.startsWith("*");
    if (form.type === TYPE_CONTELE) {
      // Na edição, chave vazia é testada com a gravada (o backend junta pelo id:
      // o endereço do Contele é fixo). Na criação, precisa vir de verdade.
      if (!editing && (blank(form.conteleToken) || blank(form.conteleApiKey))) {
        toast.error("Informe o Token e a X-API-KEY antes de testar");
        return;
      }
    } else if (form.type === TYPE_OPENAI) {
      // Aqui o endereço vem do formulário: o teste nunca usa a chave gravada.
      if (blank(form.apiKey)) {
        toast.error("Informe uma chave válida antes de testar (não vale a máscara)");
        return;
      }
    } else {
      if (!form.storeUrl.trim() || !form.consumerKey.trim()) {
        toast.error("Preencha URL, consumer key e consumer secret antes de testar");
        return;
      }
      if (blank(form.consumerSecret)) {
        toast.error("Informe o consumer secret antes de testar (não vale a máscara)");
        return;
      }
    }
    setTestingForm(true);
    try {
      const { data } = await api.post("/integrations/test-credentials", {
        type: form.type,
        config: buildPayloadConfig(form),
        ...(form.type === TYPE_CONTELE && editing ? { id: editing.id } : {}),
      });
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (err) {
      toastError(err);
    } finally {
      setTestingForm(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        isActive: form.isActive,
        config: buildPayloadConfig(form),
      };
      if (editing) {
        await api.put(`/integrations/${editing.id}`, payload);
        toast.success("Integração atualizada");
      } else {
        const { data } = await api.post("/integrations", payload);
        toast.success("Integração criada");
        if (data?.token) setTokenReveal({ name: data.name, type: data.type, token: data.token, webhookUrl: data.webhookUrl || null });
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/integrations/${pendingDelete.id}`);
      toast.success("Integração removida");
      setPendingDelete(null);
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmRegenerate = async () => {
    if (!pendingRegenerate) return;
    setRegenerating(true);
    try {
      const { data } = await api.post(`/integrations/${pendingRegenerate.id}/token`);
      setTokenReveal({
        name: pendingRegenerate.name,
        type: pendingRegenerate.type,
        token: data.token,
        webhookUrl: data.webhookUrl || null,
      });
      setPendingRegenerate(null);
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setRegenerating(false);
    }
  };

  const copyToClipboard = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copiado`);
    } catch {
      toast.error("Não consegui copiar. Selecione o texto e copie manualmente.");
    }
  };

  const handleToggle = async (item) => {
    const nextActive = !item.isActive;
    // Optimistic: flipa local antes do round-trip; se a API rejeitar (ex.: já
    // existe outra OpenAI ativa), reverte. Evita um GET /integrations cheio
    // (com subquery de productCount) por toggle.
    setItems(prev =>
      prev.map(i => (i.id === item.id ? { ...i, isActive: nextActive } : i))
    );
    try {
      await api.put(`/integrations/${item.id}`, { isActive: nextActive });
      // O estado do coletor (selo) é calculado no backend: busca de novo.
      if (item.type === TYPE_WORK_ORDERS) load();
    } catch (err) {
      setItems(prev =>
        prev.map(i => (i.id === item.id ? { ...i, isActive: item.isActive } : i))
      );
      toastError(err);
    }
  };

  const handleTest = async (item) => {
    setSavingTest(item.id);
    try {
      const { data } = await api.post(`/integrations/${item.id}/test`);
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (err) {
      toastError(err);
    } finally {
      setSavingTest(null);
    }
  };

  const handleSync = async (item) => {
    setSyncing(item.id);
    try {
      await api.post(`/integrations/${item.id}/sync`);
      toast.info("Sincronização iniciada — atualize em alguns minutos");
      setTimeout(load, 2000);
    } catch (err) {
      toastError(err);
    } finally {
      setSyncing(null);
    }
  };

  const filtered = items.filter(i =>
    !search.trim() || i.name.toLowerCase().includes(search.toLowerCase())
  );

  const renderStatusPill = (item) => {
    if (!item.isActive) {
      return (
        <span className={`${classes.statusPill} ${classes.statusInactive}`}>
          <Power size={10} /> Inativa
        </span>
      );
    }
    if (item.type === TYPE_OPENAI) {
      return (
        <span className={`${classes.statusPill} ${classes.statusOk}`}>
          <CheckCircle2 size={10} /> Ativa
        </span>
      );
    }
    if (item.lastSyncStatus === "error") {
      return (
        <span className={`${classes.statusPill} ${classes.statusError}`}>
          <AlertCircle size={10} /> Erro no sync
        </span>
      );
    }
    if (!item.lastSyncAt) {
      return (
        <span className={`${classes.statusPill} ${classes.statusPending}`}>
          <RefreshCw size={10} /> Aguardando sync
        </span>
      );
    }
    return (
      <span className={`${classes.statusPill} ${classes.statusOk}`}>
        <CheckCircle2 size={10} /> Sincronizada
      </span>
    );
  };

  const toneClass = {
    ok: classes.statusOk,
    error: classes.statusError,
    pending: classes.statusPending,
    inactive: classes.statusInactive,
  };

  // Cabeçalho dos cards que só o super mexe (fonte de OS, Contele): o super liga
  // e desliga; o admin da empresa vê o selo da Sttelar.
  const renderSuperManagedHeader = (item, Icon, subtitle) => (
    <div className={classes.cardHeader}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flex: 1, minWidth: 0 }}>
        <div className={classes.cardIcon}>
          <Icon size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={classes.cardName}>{item.name}</div>
          <div className={classes.cardUrl}>{subtitle}</div>
        </div>
      </div>
      {canManageSources ? (
        <Tooltip title={item.isActive ? "Desativar" : "Ativar"}>
          <Switch size="small" checked={item.isActive} onChange={() => handleToggle(item)} />
        </Tooltip>
      ) : (
        <Tooltip title="Configurada pela Sttelar. Para mudar algo, fale com o suporte.">
          <span className={classes.sttelarPill}>
            <Lock size={10} /> Configurada pela Sttelar
          </span>
        </Tooltip>
      )}
    </div>
  );

  // "Último sinal" (coletor) ou "Último envio" (Contele), com o selo da situação ({ label, tone, Icon }).
  const renderLastActivity = (item, label, status) => {
    const StatusIcon = status.Icon;
    return (
      <div className={classes.cardMetaRow}>
        <div>
          <strong style={{ color: theme.palette.type === "dark" ? "#f0f2f5" : "#111" }}>{label}</strong>
          <span style={{ opacity: 0.4, margin: "0 6px" }}>•</span>
          <span>{item.lastSyncAt ? formatRelativeTime(item.lastSyncAt) : "nenhum"}</span>
        </div>
        <span className={`${classes.statusPill} ${toneClass[status.tone]}`}>
          <StatusIcon size={10} /> {status.label}
        </span>
      </div>
    );
  };

  // Cartão da fonte de OS: estado do coletor no lugar de sync/credenciais.
  const renderWorkOrderSource = (item) => {
    const health = WORK_ORDER_HEALTH[item.health] || WORK_ORDER_HEALTH.never;
    const collector = item.config?.collector || {};
    const showNotice =
      (item.health === "needs_login" || item.health === "error") && item.lastSyncError;

    return (
      <>
        {renderSuperManagedHeader(item, ClipboardList, item.config?.adapterLabel || item.config?.adapter || "—")}
        {renderLastActivity(item, "Último sinal", health)}

        <div className={classes.cardInfo}>
          <span>Coletor {collector.version ? `v${collector.version}` : "—"}</span>
          <span style={{ opacity: 0.4 }}>•</span>
          <span>Token {item.config?.tokenHint || "—"}</span>
          <span style={{ opacity: 0.4 }}>•</span>
          <span>Alerta após {item.config?.alertAfterMinutes || 30} min sem sinal</span>
        </div>

        {showNotice && <div className={classes.cardNotice}>{item.lastSyncError.substring(0, 200)}</div>}

        <div className={classes.cardActions}>
          {canSeeWorkOrders && (
            <button className={classes.actionBtn} onClick={() => history.push("/work-orders")}>
              <ExternalLink size={11} /> Ver OS recebidas
            </button>
          )}
          {canManageSources && (
            <>
              <button className={classes.actionBtn} onClick={() => openEdit(item)}>
                <Edit2 size={11} /> Editar
              </button>
              <button className={classes.actionBtn} onClick={() => setPendingRegenerate(item)}>
                <KeyRound size={11} /> Gerar novo token
              </button>
              <button
                className={`${classes.actionBtn} ${classes.actionDanger}`}
                onClick={() => setPendingDelete(item)}
              >
                <Trash2 size={11} /> Excluir
              </button>
            </>
          )}
        </div>
      </>
    );
  };

  // Situação da conta do Contele pelo último envio (o card mostra só a saúde da
  // conta; o dado recusado de um cliente aparece na triagem dele).
  const conteleStatus = (item) => {
    if (!item.isActive) return { label: "Inativa", tone: "inactive", Icon: Power };
    if (item.lastSyncStatus === "error") return { label: "Com falha", tone: "error", Icon: AlertCircle };
    if (item.lastSyncAt) return { label: "Funcionando", tone: "ok", Icon: CheckCircle2 };
    return { label: "Sem envios ainda", tone: "pending", Icon: RefreshCw };
  };

  // Cartão da conta do Contele: último envio e chaves mascaradas; só o super mexe.
  const renderConteleAccount = (item) => (
    <>
      {renderSuperManagedHeader(item, MapPinned, "Contele Teams")}
      {renderLastActivity(item, "Último envio", conteleStatus(item))}

      <div className={classes.cardInfo}>
        <span>Token {item.config?.tokenMask || "—"}</span>
        <span style={{ opacity: 0.4 }}>•</span>
        <span>X-API-KEY {item.config?.apiKeyMask || "—"}</span>
      </div>

      {item.isActive && item.lastSyncStatus === "error" && item.lastSyncError && (
        <div className={classes.cardNotice}>{item.lastSyncError.substring(0, 200)}</div>
      )}

      {canManageSources && (
        <div className={classes.cardActions}>
          <button className={classes.actionBtn} onClick={() => openEdit(item)}>
            <Edit2 size={11} /> Editar
          </button>
          <button className={classes.actionBtn} onClick={() => handleTest(item)} disabled={savingTest === item.id}>
            {savingTest === item.id ? <CircularProgress size={11} /> : <CheckCircle2 size={11} />} Testar
          </button>
          <button className={`${classes.actionBtn} ${classes.actionDanger}`} onClick={() => setPendingDelete(item)}>
            <Trash2 size={11} /> Excluir
          </button>
        </div>
      )}
    </>
  );

  // Situação do Care pelo último aviso recebido.
  const careStatus = (item) => {
    if (!item.isActive) return { label: "Inativa", tone: "inactive", Icon: Power };
    if (item.lastSyncStatus === "invalid") return { label: "Aviso fora do formato", tone: "error", Icon: AlertCircle };
    if (item.lastSyncAt) return { label: "Recebendo", tone: "ok", Icon: CheckCircle2 };
    return { label: "Aguardando o primeiro aviso", tone: "pending", Icon: RefreshCw };
  };

  // Cartão do Care: último aviso e o final do endereço (o completo só aparece
  // ao criar ou ao gerar outro).
  const renderCareSource = (item) => {
    const hint = String(item.config?.tokenHint || "").replace(/^\.+/, "");
    return (
      <>
        <div className={classes.cardHeader}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flex: 1, minWidth: 0 }}>
            <div className={classes.cardIcon}>
              <Webhook size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className={classes.cardName}>{item.name}</div>
              <div className={classes.cardUrl}>Care · status das OS</div>
            </div>
          </div>
          <Tooltip title={item.isActive ? "Desativar" : "Ativar"}>
            <Switch size="small" checked={item.isActive} onChange={() => handleToggle(item)} />
          </Tooltip>
        </div>
        {renderLastActivity(item, "Último aviso", careStatus(item))}

        <div className={classes.cardInfo}>
          <span>Endereço terminado em {hint || "—"}</span>
        </div>

        {item.isActive && item.lastSyncStatus === "invalid" && item.lastSyncError && (
          <div className={classes.cardNotice}>{item.lastSyncError.substring(0, 200)}</div>
        )}

        <div className={classes.cardActions}>
          <button className={classes.actionBtn} onClick={() => openEdit(item)}>
            <Edit2 size={11} /> Editar
          </button>
          <button className={classes.actionBtn} onClick={() => setPendingRegenerate(item)}>
            <KeyRound size={11} /> Gerar novo endereço
          </button>
          <button className={`${classes.actionBtn} ${classes.actionDanger}`} onClick={() => setPendingDelete(item)}>
            <Trash2 size={11} /> Excluir
          </button>
        </div>
      </>
    );
  };

  const renderCardBody = (item) => {
    if (item.type === TYPE_CARE) return renderCareSource(item);
    if (item.type === TYPE_WORK_ORDERS) return renderWorkOrderSource(item);
    if (item.type === TYPE_CONTELE) return renderConteleAccount(item);
    const isOpenAi = item.type === TYPE_OPENAI;
    const isWoo = item.type === TYPE_WOOCOMMERCE;
    const isManaged = !!item.managed;
    const TypeIcon = isOpenAi ? Sparkles : ShoppingBag;
    const subtitle = isOpenAi
      ? (item.config?.apiKeyMask || "—")
      : (item.config?.storeUrl || "—");

    return (
      <>
        <div className={classes.cardHeader}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flex: 1, minWidth: 0 }}>
            <div className={classes.cardIcon}>
              <TypeIcon size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className={classes.cardName}>{item.name}</div>
              <div className={classes.cardUrl}>{subtitle}</div>
            </div>
          </div>
          {isManaged ? (
            <Tooltip title="IA incluída no seu plano e gerida pela Sttelar. A chave fica protegida e não pode ser editada ou removida.">
              <span className={classes.managedPill}>
                <Lock size={10} /> Gerida pela Sttelar
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={item.isActive ? "Desativar" : "Ativar"}>
              <Switch
                size="small"
                checked={item.isActive}
                onChange={() => handleToggle(item)}
              />
            </Tooltip>
          )}
        </div>

        <div className={classes.cardMetaRow}>
          <div>
            {isOpenAi ? (
              <>
                <strong style={{ color: theme.palette.type === "dark" ? "#f0f2f5" : "#111" }}>OpenAI</strong>
                <span style={{ opacity: 0.4, margin: "0 6px" }}>•</span>
                <span>{item.config?.baseUrl ? "Endpoint custom" : "api.openai.com"}</span>
              </>
            ) : (
              <>
                <strong style={{ color: theme.palette.type === "dark" ? "#f0f2f5" : "#111" }}>
                  {item.productCount || 0}
                </strong>
                {" produtos"}
                <span style={{ opacity: 0.4, margin: "0 6px" }}>•</span>
                <span>sync {formatRelativeTime(item.lastSyncAt)}</span>
              </>
            )}
          </div>
          {renderStatusPill(item)}
        </div>

        {item.lastSyncStatus === "error" && item.lastSyncError && (
          <div style={{
            padding: 8,
            borderRadius: 6,
            background: "rgba(239, 68, 68, 0.08)",
            fontSize: 10,
            color: "#ef4444",
            wordBreak: "break-word",
          }}>
            {item.lastSyncError.substring(0, 200)}
          </div>
        )}

        {/* Integração GERIDA pela Sttelar = 100% read-only: nenhuma ação (nem
            "Ver uso"/"Testar"). As demais mantêm suas ações normais. */}
        {!isManaged && (
          <div className={classes.cardActions}>
            {isOpenAi && (
              <button
                className={classes.actionBtn}
                onClick={() => setUsageItem(item)}
              >
                <BarChart3 size={11} /> Ver uso
              </button>
            )}
            <button className={classes.actionBtn} onClick={() => openEdit(item)}>
              <Edit2 size={11} /> Editar
            </button>
            <button
              className={classes.actionBtn}
              onClick={() => handleTest(item)}
              disabled={savingTest === item.id}
            >
              {savingTest === item.id ? <CircularProgress size={11} /> : <CheckCircle2 size={11} />} Testar
            </button>
            {isWoo && (
              <button
                className={classes.actionBtn}
                onClick={() => handleSync(item)}
                disabled={syncing === item.id || !item.isActive}
              >
                {syncing === item.id
                  ? <CircularProgress size={11} />
                  : <RefreshCw size={11} />}
                {" Sincronizar"}
              </button>
            )}
            <button
              className={`${classes.actionBtn} ${classes.actionDanger}`}
              onClick={() => setPendingDelete(item)}
            >
              <Trash2 size={11} /> Excluir
            </button>
          </div>
        )}
      </>
    );
  };

  const careRevealUrl =
    tokenReveal?.webhookUrl || (tokenReveal?.token ? `${String(API_BASE_URL).replace(/\/+$/, "")}/webhook/care/${tokenReveal.token}` : "");

  const typeMeta = INTEGRATION_TYPES.find(t => t.id === form.type) || INTEGRATION_TYPES[0];
  const isWorkOrderForm = form.type === TYPE_WORK_ORDERS;
  const isCareForm = form.type === TYPE_CARE;
  const modalTitle = isWorkOrderForm
    ? editing ? "Editar fonte de OS" : "Nova fonte de OS"
    : editing
    ? `Editar integração ${typeMeta.label}`
    : `Nova integração ${typeMeta.label}`;
  const modalSubtitle = editing
    ? typeMeta.description
    : isWorkOrderForm
    ? "Preencha os campos abaixo. O token do coletor é gerado ao criar."
    : isCareForm
    ? "O endereço para colar no Care é gerado ao criar."
    : "Preencha os campos abaixo e teste antes de salvar.";

  // `autoComplete` controla o autofill do navegador. Para campos com
  // type=password, Chrome só respeita "new-password" (e ignora "off"). Em
  // campos de texto, "off" + um `name` não-padrão bloqueiam o autofill de
  // credenciais salvas (admin@sttelar.pro etc). Sem isso, o navegador
  // popularia "Nome" com o email do login e "API Key" com a senha.
  const renderInput = ({
    value,
    onChange,
    placeholder,
    type = "text",
    suffix = null,
    autoComplete = "off",
    name,
  }) => (
    <div className={classes.formInputWrap}>
      <InputBase
        className={classes.formInput}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        inputProps={{
          autoComplete,
          ...(name ? { name } : {}),
          "data-lpignore": "true",
          "data-1p-ignore": "true",
        }}
      />
      {suffix}
    </div>
  );

  return (
    <MainContainer>
      <div className={classes.page}>
        <div className={classes.header}>
          <div>
            <div className={classes.title}>
              <Cable size={22} />
              Integrações
            </div>
            <div className={classes.subtitle}>
              Conecte serviços externos por empresa: catálogo de produtos (WooCommerce), modelo de IA (OpenAI), ordens de serviço, etc.
            </div>
          </div>
          <button className={classes.btnPrimary} onClick={openTypePick}>
            <Plus size={14} /> Nova integração
          </button>
        </div>

        {!loading && items.length > 5 && (
          <div style={{ marginBottom: 16, position: "relative", maxWidth: 360 }}>
            <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#a0aec0" }} />
            <input
              type="text"
              placeholder="Buscar por nome..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={classes.searchInput}
            />
          </div>
        )}

        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <CircularProgress size={28} />
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className={classes.empty}>
            <Cable size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Nenhuma integração cadastrada</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Clique em "Nova integração" para conectar seu primeiro serviço.</div>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className={classes.grid}>
            {filtered.map(item => (
              <div key={item.id} className={classes.card}>
                {renderCardBody(item)}
              </div>
            ))}
          </div>
        )}

        {/* Modal A — seletor de tipo */}
        <Dialog
          open={typePickOpen}
          onClose={() => setTypePickOpen(false)}
          className={classes.modal}
          maxWidth={false}
        >
          <div className={classes.modalHeader}>
            <div className={classes.modalHeaderText}>
              <Typography className={classes.modalTitle}>Nova integração</Typography>
              <Typography className={classes.modalSubtitle}>
                Escolha o tipo de serviço para conectar
              </Typography>
            </div>
            <IconButton
              size="small"
              className={classes.modalClose}
              onClick={() => setTypePickOpen(false)}
            >
              <X size={18} strokeWidth={1.75} />
            </IconButton>
          </div>

          <div className={classes.modalContent}>
            <div className={classes.typePickList}>
              {INTEGRATION_TYPES.filter(t => !t.superOnly || canManageSources).map(t => {
                const { Icon, accent: cardAccent } = t;
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={classes.typePickCard}
                    onClick={() => pickType(t.id)}
                  >
                    <div
                      className={classes.typePickIconWrap}
                      style={{ background: `${cardAccent}1f`, color: cardAccent }}
                    >
                      <Icon size={22} strokeWidth={1.8} />
                    </div>
                    <div className={classes.typePickBody}>
                      <div className={classes.typePickName}>{t.label}</div>
                      <div className={classes.typePickDescription}>{t.description}</div>
                    </div>
                    <ChevronRight size={18} className={classes.typePickChevron} />
                  </button>
                );
              })}
            </div>
          </div>

          <div className={classes.modalActions}>
            <Button
              className={classes.modalCancelBtn}
              onClick={() => setTypePickOpen(false)}
            >
              Cancelar
            </Button>
          </div>
        </Dialog>

        {/* Modal B — formulário específico por tipo */}
        <Dialog
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          className={classes.modal}
          maxWidth={false}
        >
          <div className={classes.modalHeader}>
            <div className={classes.modalHeaderText}>
              <Typography className={classes.modalTitle}>{modalTitle}</Typography>
              <Typography className={classes.modalSubtitle}>{modalSubtitle}</Typography>
            </div>
            <IconButton
              size="small"
              className={classes.modalClose}
              onClick={() => setModalOpen(false)}
            >
              <X size={18} strokeWidth={1.75} />
            </IconButton>
          </div>

          <div className={classes.modalContent}>
            <div className={classes.formField}>
              <span className={classes.formLabel}>Nome</span>
              {renderInput({
                value: form.name,
                onChange: e => setForm({ ...form, name: e.target.value }),
                placeholder:
                  form.type === TYPE_OPENAI
                    ? "Ex: Conta Produção"
                    : form.type === TYPE_WORK_ORDERS
                    ? "Ex: Coletor ICS do posto"
                    : form.type === TYPE_CONTELE
                    ? "Ex: Contele JMV"
                    : form.type === TYPE_CARE
                    ? "Ex: Care JMV"
                    : "Ex: Loja Principal",
                name: "integration-label",
              })}
            </div>

            {form.type === TYPE_CARE ? (
              <div className={classes.formField}>
                <span className={classes.formHelp}>
                  Depois de criar, cole o endereço no webhook do Care. Os avisos que chegarem aparecem como status no
                  gatilho "Status da OS" dos fluxos.
                </span>
              </div>
            ) : form.type === TYPE_WORK_ORDERS ? (
              <>
                <div className={classes.formField}>
                  <span className={classes.formLabel}>Origem das OS</span>
                  <ModernSelect
                    size="small"
                    fullWidth
                    className={classes.formSelect}
                    value={form.adapter}
                    onChange={e => setForm({ ...form, adapter: e.target.value })}
                    inputProps={{ "aria-label": "Origem das OS" }}
                  >
                    <MenuItem value="midea-ics">Midea (portal ICS)</MenuItem>
                    <MenuItem value="samsung-gspn">Samsung (portal GSPN)</MenuItem>
                  </ModernSelect>
                  <span className={classes.formHelp}>
                    Sistema do fabricante de onde o coletor do posto lê as OS.
                  </span>
                </div>

                <div className={classes.formField}>
                  <span className={classes.formLabel}>Alerta sem sinal (minutos)</span>
                  {renderInput({
                    value: form.alertAfterMinutes,
                    onChange: e => setForm({ ...form, alertAfterMinutes: e.target.value }),
                    placeholder: "30",
                    type: "number",
                  })}
                  <span className={classes.formHelp}>
                    Depois desse tempo sem notícia do coletor, a tela de OS e o menu mostram um aviso. Entre 15 e 1440.
                  </span>
                </div>

              </>
            ) : form.type === TYPE_CONTELE ? (
              <>
                <div className={classes.formField}>
                  <span className={classes.formLabel}>Token</span>
                  {renderInput({
                    value: form.conteleToken,
                    onChange: e => setForm({ ...form, conteleToken: e.target.value }),
                    placeholder: editing ? "Deixe em branco para manter o atual" : "Cole o Token (Authorization)",
                    type: showConteleToken ? "text" : "password",
                    autoComplete: "new-password",
                    name: "contele-token",
                    suffix: (
                      <IconButton
                        size="small"
                        className={classes.formInputAction}
                        onClick={() => setShowConteleToken(s => !s)}
                        aria-label={showConteleToken ? "Esconder o Token" : "Mostrar o Token"}
                      >
                        {showConteleToken ? <EyeOff size={16} /> : <Eye size={16} />}
                      </IconButton>
                    ),
                  })}
                  {editing && <span className={classes.formHelp}>Atual: {editing.config?.tokenMask || "—"}</span>}
                </div>

                <div className={classes.formField}>
                  <span className={classes.formLabel}>X-API-KEY</span>
                  {renderInput({
                    value: form.conteleApiKey,
                    onChange: e => setForm({ ...form, conteleApiKey: e.target.value }),
                    placeholder: editing ? "Deixe em branco para manter a atual" : "Cole a X-API-KEY",
                    type: showConteleApiKey ? "text" : "password",
                    autoComplete: "new-password",
                    name: "contele-api-key",
                    suffix: (
                      <IconButton
                        size="small"
                        className={classes.formInputAction}
                        onClick={() => setShowConteleApiKey(s => !s)}
                        aria-label={showConteleApiKey ? "Esconder a X-API-KEY" : "Mostrar a X-API-KEY"}
                      >
                        {showConteleApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                      </IconButton>
                    ),
                  })}
                  <span className={classes.formHelp}>
                    {editing ? `Atual: ${editing.config?.apiKeyMask || "—"}. ` : ""}
                    As duas chaves ficam no Contele, em Configurações → Integração → Usar API Rest. Só um administrador da conta do Contele vê.
                  </span>
                </div>
              </>
            ) : form.type === TYPE_OPENAI ? (
              <>
                <div className={classes.formField}>
                  <span className={classes.formLabel}>API Key</span>
                  {renderInput({
                    value: form.apiKey,
                    onChange: e => setForm({ ...form, apiKey: e.target.value }),
                    placeholder: editing ? "Deixe em branco para manter a atual" : "sk-...",
                    type: showApiKey ? "text" : "password",
                    autoComplete: "new-password",
                    name: "openai-token",
                    suffix: (
                      <IconButton
                        size="small"
                        className={classes.formInputAction}
                        onClick={() => setShowApiKey(s => !s)}
                      >
                        {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                      </IconButton>
                    ),
                  })}
                  <span className={classes.formHelp}>
                    Crie em platform.openai.com → API keys. Precisa de permissão para chat completions.
                  </span>
                </div>

                <div className={classes.formField}>
                  <span className={classes.formLabel}>Organization ID (opcional)</span>
                  {renderInput({
                    value: form.organizationId,
                    onChange: e => setForm({ ...form, organizationId: e.target.value }),
                    placeholder: "org-...",
                  })}
                  <span className={classes.formHelp}>
                    Só preencha se a chave pertence a uma organização específica.
                  </span>
                </div>

                <div className={classes.formField}>
                  <span className={classes.formLabel}>Base URL (opcional)</span>
                  {renderInput({
                    value: form.baseUrl,
                    onChange: e => setForm({ ...form, baseUrl: e.target.value }),
                    placeholder: "https://api.openai.com/v1",
                  })}
                  <span className={classes.formHelp}>
                    Para proxies, Azure OpenAI ou endpoints compatíveis. Em branco usa OpenAI oficial.
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className={classes.formField}>
                  <span className={classes.formLabel}>URL da loja</span>
                  {renderInput({
                    value: form.storeUrl,
                    onChange: e => setForm({ ...form, storeUrl: e.target.value }),
                    placeholder: "https://minhaloja.com.br",
                  })}
                  <span className={classes.formHelp}>
                    URL base do site WooCommerce (sem /wp-json).
                  </span>
                </div>

                <div className={classes.formField}>
                  <span className={classes.formLabel}>Consumer Key</span>
                  {renderInput({
                    value: form.consumerKey,
                    onChange: e => setForm({ ...form, consumerKey: e.target.value }),
                    placeholder: "ck_xxxxxxxxxxxxxxxxxxxxxxxx",
                  })}
                </div>

                <div className={classes.formField}>
                  <span className={classes.formLabel}>Consumer Secret</span>
                  {renderInput({
                    value: form.consumerSecret,
                    onChange: e => setForm({ ...form, consumerSecret: e.target.value }),
                    placeholder: editing ? "Deixe em branco para manter o atual" : "cs_xxxxxxxxxxxxxxxxxxxxxxxx",
                    type: showConsumerSecret ? "text" : "password",
                    autoComplete: "new-password",
                    name: "woocommerce-secret",
                    suffix: (
                      <IconButton
                        size="small"
                        className={classes.formInputAction}
                        onClick={() => setShowConsumerSecret(s => !s)}
                      >
                        {showConsumerSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                      </IconButton>
                    ),
                  })}
                  <span className={classes.formHelp}>
                    Crie em WooCommerce → Configurações → Avançado → REST API com permissão de leitura.
                  </span>
                </div>

                <div className={classes.formField}>
                  <span className={classes.formLabel}>Intervalo de sync (horas)</span>
                  {renderInput({
                    value: form.syncIntervalHours,
                    onChange: e => setForm({ ...form, syncIntervalHours: e.target.value }),
                    placeholder: "24",
                    type: "number",
                  })}
                  <span className={classes.formHelp}>
                    A cada quantas horas o catálogo é resincronizado. Padrão: 24h.
                  </span>
                </div>
              </>
            )}

            <div className={classes.formActiveRow}>
              <Switch
                checked={form.isActive}
                onChange={e => setForm({ ...form, isActive: e.target.checked })}
                size="small"
                color="primary"
              />
              <span className={classes.formActiveLabel}>Integração ativa</span>
            </div>
          </div>

          <div className={classes.modalActions}>
            <Button
              className={classes.modalCancelBtn}
              onClick={() => setModalOpen(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            {form.type !== TYPE_WORK_ORDERS && form.type !== TYPE_CARE && (
              <Button
                className={classes.modalGhostBtn}
                onClick={handleTestForm}
                disabled={saving || testingForm}
                startIcon={testingForm
                  ? <CircularProgress size={14} />
                  : <CheckCircle2 size={14} strokeWidth={2} />}
              >
                Testar conexão
              </Button>
            )}
            <Button
              className={classes.modalPrimaryBtn}
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : editing ? "Salvar" : "Criar"}
            </Button>
          </div>
        </Dialog>

        <ConfirmationModal
          open={!!pendingDelete}
          onClose={() => !deleting && setPendingDelete(null)}
          onConfirm={handleConfirmDelete}
          confirmLoading={deleting}
          title={
            pendingDelete
              ? `Remover integração "${pendingDelete.name}"?`
              : ""
          }
        >
          {pendingDelete?.type === TYPE_WORK_ORDERS
            ? "Esta ação remove a fonte e invalida o token do coletor: ele deixa de enviar OS. As OS já recebidas continuam na tela de Ordens de serviço."
            : pendingDelete?.type === TYPE_CARE
            ? "Esta ação remove a integração e invalida o endereço: o Care deixa de avisar, e os fluxos com o gatilho \"Status da OS\" dela param de enviar."
            : pendingDelete?.type === TYPE_CONTELE
            ? "Esta ação remove a conta do Contele desta empresa: as triagens deixam de criar Locais nela. O que já foi enviado continua no Contele."
            : pendingDelete?.type === TYPE_OPENAI
            ? "Esta ação remove a chave OpenAI desta empresa. Features de IA ficarão indisponíveis até cadastrar outra integração ativa."
            : "Esta ação remove a integração e todos os produtos sincronizados. Não pode ser desfeita."}
        </ConfirmationModal>

        <ConfirmationModal
          open={!!pendingRegenerate}
          onClose={() => !regenerating && setPendingRegenerate(null)}
          onConfirm={handleConfirmRegenerate}
          confirmLoading={regenerating}
          title={
            pendingRegenerate
              ? pendingRegenerate.type === TYPE_CARE
                ? `Gerar novo endereço para "${pendingRegenerate.name}"?`
                : `Gerar novo token para "${pendingRegenerate.name}"?`
              : ""
          }
        >
          {pendingRegenerate?.type === TYPE_CARE
            ? "O endereço atual deixa de valer na hora: o Care para de avisar até você colar o novo nele."
            : "O token atual deixa de valer na hora: o coletor para de enviar OS até receber o novo."}
        </ConfirmationModal>

        {/* Token da fonte de OS ou endereço do Care: aparece uma única vez (no banco fica só o hash).
            Sem o endereço do servidor (BACKEND_URL), o do Care é montado com o da API desta tela. */}
        <Dialog
          open={!!tokenReveal}
          // Clique fora não fecha: o token some para sempre ao fechar.
          onClose={(_, reason) => reason !== "backdropClick" && setTokenReveal(null)}
          className={classes.modal}
          maxWidth={false}
        >
          <div className={classes.modalHeader}>
            <div className={classes.modalHeaderText}>
              <Typography className={classes.modalTitle}>
                {tokenReveal?.type === TYPE_CARE ? "Endereço para o Care" : "Token do coletor"}
              </Typography>
              <Typography className={classes.modalSubtitle}>{tokenReveal?.name}</Typography>
            </div>
            <IconButton size="small" className={classes.modalClose} onClick={() => setTokenReveal(null)}>
              <X size={18} strokeWidth={1.75} />
            </IconButton>
          </div>

          {tokenReveal?.type === TYPE_CARE ? (
            <div className={classes.modalContent}>
              <div className={classes.tokenWarning}>
                <strong>Copie agora.</strong> Este endereço não aparece de novo. Se perder, gere outro no cartão da
                integração. Quem tiver o endereço consegue mandar avisos: não o compartilhe.
              </div>

              <div className={classes.formField}>
                <span className={classes.formLabel}>Endereço do webhook</span>
                <div className={classes.formInputWrap}>
                  <span className={classes.tokenValue} style={{ fontFamily: "ui-monospace, Menlo, Consolas, monospace" }}>
                    {careRevealUrl}
                  </span>
                  <IconButton
                    size="small"
                    className={classes.formInputAction}
                    onClick={() => copyToClipboard(careRevealUrl, "Endereço")}
                    aria-label="Copiar o endereço"
                  >
                    <Copy size={16} />
                  </IconButton>
                </div>
                <span className={classes.formHelp}>
                  No Care, cadastre este endereço no webhook de atualização de OS, no lugar do endereço do N8N.
                </span>
              </div>
            </div>
          ) : (
            <div className={classes.modalContent}>
              <div className={classes.tokenWarning}>
                <strong>Copie agora.</strong> Este token não aparece de novo. Se perder, gere outro no cartão da
                integração. Guarde-o só no computador do coletor.
              </div>

              <div className={classes.formField}>
                <span className={classes.formLabel}>Endereço do Sttelar Talk</span>
                <div className={classes.formInputWrap}>
                  <span className={classes.tokenValue} style={{ fontFamily: "ui-monospace, Menlo, Consolas, monospace" }}>
                    {API_BASE_URL}
                  </span>
                  <IconButton
                    size="small"
                    className={classes.formInputAction}
                    onClick={() => copyToClipboard(API_BASE_URL, "Endereço")}
                  >
                    <Copy size={16} />
                  </IconButton>
                </div>
              </div>

              <div className={classes.formField}>
                <span className={classes.formLabel}>Token</span>
                <div className={classes.formInputWrap}>
                  <span className={classes.tokenValue} style={{ fontFamily: "ui-monospace, Menlo, Consolas, monospace" }}>
                    {tokenReveal?.token}
                  </span>
                  <IconButton
                    size="small"
                    className={classes.formInputAction}
                    onClick={() => copyToClipboard(tokenReveal?.token || "", "Token")}
                  >
                    <Copy size={16} />
                  </IconButton>
                </div>
                <span className={classes.formHelp}>
                  No computador do coletor, rode <code>npm.cmd run configurar</code> e cole o endereço e o token.
                </span>
              </div>
            </div>
          )}

          <div className={classes.modalActions}>
            <Button className={classes.modalPrimaryBtn} variant="contained" onClick={() => setTokenReveal(null)}>
              Já copiei
            </Button>
          </div>
        </Dialog>

        <OpenAiUsageModal
          open={!!usageItem}
          onClose={() => setUsageItem(null)}
          integration={usageItem}
        />
      </div>
    </MainContainer>
  );
};

export default Integrations;
